<!-- src/lib/components/DrivePane.svelte -->
<!--
  Google Drive integration pane. Three visible states:

    1. NOT CONFIGURED — the server is missing GOOGLE_OAUTH_CLIENT_ID /
       GOOGLE_OAUTH_CLIENT_SECRET env vars. Shows setup instructions
       so the user knows what to configure.

    2. NOT CONNECTED — the server is configured but this workspace
       hasn't authorized Drive yet. Big "Connect Google Drive" button
       that opens the consent URL in a new window.

    3. CONNECTED — file browser showing root directory files. Search
       box at top, pagination via Drive's page_token, click-to-preview
       for supported types (opens in a new tab via Drive's webViewLink).

  The user completes OAuth in a new window. When the callback page
  renders, this pane polls /api/v1/oauth/google/status every 1.5s
  until it sees `connected: true`, then loads the file list.
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';

  interface AuthStatus {
    configured: boolean;
    connected:  boolean;
    expires_at: string | null;
    scope:      string | null;
  }

  interface DriveFile {
    id:             string;
    name:           string;
    mimeType:       string;
    iconLink?:      string;
    thumbnailLink?: string;
    webViewLink?:   string;
    modifiedTime?:  string;
    size?:          string;
    parents?:       string[];
    owners?:        Array<{ displayName: string; emailAddress: string; photoLink?: string }>;
  }

  interface ListResponse {
    files:          DriveFile[];
    nextPageToken?: string;
  }

  let status = $state<AuthStatus>({ configured: false, connected: false, expires_at: null, scope: null });
  let statusLoading = $state(true);
  let files = $state<DriveFile[]>([]);
  let nextPageToken = $state<string | null>(null);
  let filesLoading = $state(false);
  let errorMsg = $state<string | null>(null);
  let searchQuery = $state('');

  let pollTimer: ReturnType<typeof setInterval> | null = null;

  async function refreshStatus() {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) return;
    try {
      status = await api<AuthStatus>(`/api/v1/oauth/google/status?workspace_id=${wsId}`);
      statusLoading = false;
      if (status.connected && files.length === 0) {
        void loadFiles();
      }
    } catch (e) {
      console.warn('drive status failed:', e);
      statusLoading = false;
    }
  }

  async function loadFiles(resetPagination = true) {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId || !status.connected) return;
    filesLoading = true;
    errorMsg = null;
    try {
      const params = new URLSearchParams({ workspace_id: wsId });
      if (searchQuery.trim()) {
        // Drive search query: https://developers.google.com/drive/api/guides/search-files
        params.set('q', `name contains '${searchQuery.trim().replace(/'/g, "\\'")}' and trashed = false`);
      }
      if (!resetPagination && nextPageToken) {
        params.set('page_token', nextPageToken);
      }
      const res: ListResponse = await api(`/api/v1/drive/files?${params.toString()}`);
      if (resetPagination) {
        files = res.files ?? [];
      } else {
        files = [...files, ...(res.files ?? [])];
      }
      nextPageToken = res.nextPageToken ?? null;
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to load Drive files';
    } finally {
      filesLoading = false;
    }
  }

  async function connect() {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) return;
    try {
      const res = await api<{ auth_url: string }>(`/api/v1/oauth/google/start?workspace_id=${wsId}`);
      // Open Google consent in a new window so the user keeps their app state
      window.open(res.auth_url, 'google-oauth', 'width=500,height=700');

      // Start polling the status endpoint every 1.5s until we see connected:true
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = setInterval(async () => {
        await refreshStatus();
        if (status.connected && pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
      }, 1500);
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to start OAuth';
    }
  }

  async function disconnect() {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) return;
    if (!confirm('Disconnect Google Drive? Your tokens will be removed.')) return;
    try {
      await api(`/api/v1/oauth/google/disconnect?workspace_id=${wsId}`, { method: 'DELETE' });
      files = [];
      nextPageToken = null;
      await refreshStatus();
    } catch (e) {
      console.warn('disconnect failed:', e);
    }
  }

  function handleSearchKey(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void loadFiles(true);
    }
  }

  function fileIcon(mime: string): string {
    if (mime.includes('folder'))      return '▸';
    if (mime.includes('document'))    return '≡';
    if (mime.includes('spreadsheet')) return '⊞';
    if (mime.includes('presentation'))return '◈';
    if (mime.startsWith('image/'))    return '▥';
    if (mime.startsWith('video/'))    return '▶';
    if (mime.startsWith('audio/'))    return '♪';
    if (mime.includes('pdf'))         return '◪';
    return '○';
  }

  function formatBytes(bytes: string | undefined): string {
    if (!bytes) return '';
    const n = parseInt(bytes, 10);
    if (isNaN(n)) return '';
    if (n < 1024)         return `${n} B`;
    if (n < 1024 * 1024)  return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  function formatDate(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays < 7)   return `${diffDays}d ago`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  function openInDrive(file: DriveFile) {
    if (file.webViewLink) {
      window.open(file.webViewLink, '_blank', 'noopener,noreferrer');
    }
  }

  // Reload status when the active workspace changes
  $effect(() => {
    void pmStore.activeWorkspaceId;
    statusLoading = true;
    files = [];
    void refreshStatus();
  });

  onMount(() => {
    void refreshStatus();
  });

  onDestroy(() => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  });
</script>

<div class="drive-pane">
  {#if statusLoading}
    <div class="state-empty">
      <p>Checking Drive connection…</p>
    </div>

  {:else if !status.configured}
    <div class="state-empty state-setup">
      <h2>Google Drive not configured</h2>
      <p class="hint">To enable Drive integration, set these environment variables on the backend and restart it:</p>
      <div class="env-list">
        <div class="env-var">
          <span class="env-key">GOOGLE_OAUTH_CLIENT_ID</span>
          <span class="env-desc">OAuth client ID from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener">Google Cloud Console</a></span>
        </div>
        <div class="env-var">
          <span class="env-key">GOOGLE_OAUTH_CLIENT_SECRET</span>
          <span class="env-desc">Client secret for the same OAuth client</span>
        </div>
        <div class="env-var">
          <span class="env-key">GOOGLE_OAUTH_REDIRECT_URI</span>
          <span class="env-desc">Defaults to <code>http://localhost:3000/api/v1/oauth/google/callback</code>. Add this exact URI to your OAuth client's authorized redirect URIs.</span>
        </div>
      </div>
      <p class="hint">Setup steps:</p>
      <ol class="steps">
        <li>Go to Google Cloud Console → APIs & Services → Credentials</li>
        <li>Create a new OAuth 2.0 Client ID (Desktop app or Web application)</li>
        <li>Enable the <strong>Google Drive API</strong> in the project</li>
        <li>Add the redirect URI above</li>
        <li>Copy the client ID and secret into the env vars</li>
        <li>Restart <code>agentboard-server</code></li>
      </ol>
    </div>

  {:else if !status.connected}
    <div class="state-empty">
      <div class="drive-icon-big">▲</div>
      <h2>Connect Google Drive</h2>
      <p class="hint">Browse, search, and attach Drive files from inside AgentBoard.</p>
      <button class="connect-btn" onclick={connect}>
        Connect Google Drive
      </button>
      {#if errorMsg}
        <p class="error-text">{errorMsg}</p>
      {/if}
      <p class="micro-hint">A new window will open for Google consent. It polls every 1.5s for completion.</p>
    </div>

  {:else}
    <div class="drive-header">
      <div class="header-left">
        <h2 class="drive-title">Google Drive</h2>
        {#if status.scope}
          <span class="scope-tag">{status.scope.replace('https://www.googleapis.com/auth/', '')}</span>
        {/if}
      </div>
      <div class="header-actions">
        <input
          class="search-input"
          bind:value={searchQuery}
          onkeydown={handleSearchKey}
          placeholder="Search Drive…"
        />
        <button class="refresh-btn" onclick={() => loadFiles(true)} disabled={filesLoading}>
          {filesLoading ? '…' : '↻'}
        </button>
        <button class="disconnect-btn" onclick={disconnect} title="Disconnect Drive">
          Disconnect
        </button>
      </div>
    </div>

    {#if errorMsg}
      <div class="error-banner">{errorMsg}</div>
    {/if}

    {#if filesLoading && files.length === 0}
      <div class="state-empty"><p>Loading files…</p></div>
    {:else if files.length === 0}
      <div class="state-empty">
        <p class="hint">No files found.</p>
      </div>
    {:else}
      <div class="file-list">
        {#each files as f (f.id)}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div class="file-row" onclick={() => openInDrive(f)} title={f.name}>
            {#if f.thumbnailLink}
              <img class="file-thumb" src={f.thumbnailLink} alt="" referrerpolicy="no-referrer" loading="lazy" />
            {:else}
              <div class="file-thumb-placeholder">{fileIcon(f.mimeType)}</div>
            {/if}
            <div class="file-info">
              <div class="file-name">{f.name}</div>
              <div class="file-meta">
                <span class="file-mime">{f.mimeType.replace('application/vnd.google-apps.', '')}</span>
                {#if f.size}<span>· {formatBytes(f.size)}</span>{/if}
                {#if f.modifiedTime}<span>· {formatDate(f.modifiedTime)}</span>{/if}
              </div>
            </div>
            {#if f.owners && f.owners.length > 0}
              <div class="file-owner">{f.owners[0].displayName}</div>
            {/if}
          </div>
        {/each}
      </div>
      {#if nextPageToken}
        <div class="load-more-wrap">
          <button class="load-more-btn" onclick={() => loadFiles(false)} disabled={filesLoading}>
            {filesLoading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      {/if}
    {/if}
  {/if}
</div>

<style>
  .drive-pane {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    color: oklch(0.85 0.04 310);
    overflow: hidden;
  }

  /* ── Empty states (not configured, not connected, loading) ─────── */
  .state-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    color: oklch(0.72 0.04 310 / 0.75);
  }
  .state-empty h2 {
    color: oklch(0.94 0.08 310);
    margin: 0.5rem 0;
    font-size: 1.3rem;
    font-weight: 600;
  }
  .state-empty .hint {
    color: oklch(0.65 0.04 310 / 0.7);
    margin: 0.25rem 0 1rem;
    max-width: 480px;
  }
  .state-empty .micro-hint {
    color: oklch(0.55 0.04 310 / 0.5);
    font-size: 0.75rem;
    margin-top: 1.5rem;
  }

  .drive-icon-big {
    font-size: 3rem;
    color: oklch(0.78 0.26 310 / 0.75);
    margin-bottom: 0.5rem;
  }

  .connect-btn {
    background: oklch(0.72 0.28 310 / 0.16);
    border: 1px solid oklch(0.72 0.28 310 / 0.4);
    color: oklch(0.92 0.14 310);
    padding: 0.75rem 1.75rem;
    border-radius: 7px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .connect-btn:hover {
    background: oklch(0.72 0.28 310 / 0.26);
    border-color: oklch(0.80 0.26 310 / 0.6);
    color: oklch(0.96 0.18 310);
  }

  .state-setup {
    justify-content: flex-start;
    padding-top: 3rem;
    max-width: 640px;
    margin: 0 auto;
    text-align: left;
    align-items: stretch;
  }
  .env-list {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin: 1rem 0;
  }
  .env-var {
    display: flex;
    flex-direction: column;
    background: oklch(0.12 0.02 275 / 0.60);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 6px;
    padding: 0.625rem 0.875rem;
  }
  .env-key {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.78rem;
    color: oklch(0.88 0.18 310);
    font-weight: 600;
  }
  .env-desc {
    font-size: 0.76rem;
    color: oklch(0.65 0.04 310 / 0.7);
    margin-top: 0.2rem;
  }
  .env-desc a {
    color: oklch(0.78 0.26 310);
    text-decoration: none;
  }
  .env-desc a:hover { text-decoration: underline; }
  .env-desc code {
    background: oklch(0.08 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.3);
    padding: 0.05rem 0.3rem;
    border-radius: 3px;
    font-size: 0.72rem;
  }
  .steps {
    color: oklch(0.72 0.04 310 / 0.8);
    font-size: 0.85rem;
    line-height: 1.6;
    padding-left: 1.25rem;
  }
  .steps strong { color: oklch(0.90 0.10 310); }
  .steps code {
    background: oklch(0.08 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.3);
    padding: 0.05rem 0.3rem;
    border-radius: 3px;
    font-size: 0.76rem;
    font-family: 'JetBrains Mono', monospace;
  }

  /* ── Connected header ─────────────────────────────────────────── */
  .drive-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem 0.75rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.15);
    gap: 1rem;
  }
  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .drive-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: oklch(0.94 0.06 310);
  }
  .scope-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: oklch(0.78 0.16 85 / 0.85);
    background: oklch(0.78 0.16 85 / 0.10);
    border: 1px solid oklch(0.78 0.16 85 / 0.28);
    border-radius: 3px;
    padding: 0.1rem 0.4rem;
  }
  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .search-input {
    background: oklch(0.14 0.02 275 / 0.70);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    color: oklch(0.92 0.03 310);
    padding: 0.4rem 0.7rem;
    border-radius: 5px;
    font-size: 0.78rem;
    outline: none;
    width: 220px;
    transition: border-color 100ms ease;
  }
  .search-input:focus { border-color: oklch(0.72 0.28 310 / 0.45); }

  .refresh-btn,
  .disconnect-btn {
    background: oklch(0.14 0.02 275 / 0.60);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    color: oklch(0.72 0.04 310 / 0.75);
    padding: 0.4rem 0.75rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.78rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .refresh-btn:hover:not(:disabled),
  .disconnect-btn:hover {
    background: oklch(0.18 0.03 275);
    color: oklch(0.92 0.04 310);
    border-color: oklch(0.30 0.05 275 / 0.4);
  }
  .refresh-btn { padding: 0.4rem 0.55rem; font-size: 0.9rem; }

  .error-banner {
    background: oklch(0.22 0.14 25 / 0.30);
    border-bottom: 1px solid oklch(0.62 0.26 25 / 0.4);
    color: oklch(0.85 0.15 25);
    padding: 0.6rem 1.5rem;
    font-size: 0.78rem;
  }
  .error-text {
    color: oklch(0.82 0.22 25 / 0.85);
    margin-top: 1rem;
    font-size: 0.8rem;
  }

  /* ── File list ────────────────────────────────────────────────── */
  .file-list {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0;
  }
  .file-list::-webkit-scrollbar { width: 8px; }
  .file-list::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.4);
    border-radius: 4px;
  }
  .file-row {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.6rem 1.5rem;
    cursor: pointer;
    transition: background-color 100ms ease;
    contain: layout paint;
  }
  .file-row:hover {
    background: oklch(0.72 0.28 310 / 0.07);
  }
  .file-thumb,
  .file-thumb-placeholder {
    width: 36px;
    height: 36px;
    border-radius: 5px;
    flex-shrink: 0;
    background: oklch(0.10 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .file-thumb {
    object-fit: cover;
  }
  .file-thumb-placeholder {
    color: oklch(0.65 0.08 310 / 0.6);
    font-size: 1rem;
  }
  .file-info {
    flex: 1;
    min-width: 0;
  }
  .file-name {
    font-size: 0.86rem;
    color: oklch(0.92 0.04 310);
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .file-meta {
    font-size: 0.7rem;
    color: oklch(0.55 0.04 310 / 0.55);
    margin-top: 0.15rem;
    display: flex;
    gap: 0.4rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .file-mime {
    font-family: 'JetBrains Mono', monospace;
  }
  .file-owner {
    font-size: 0.72rem;
    color: oklch(0.55 0.04 310 / 0.55);
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .load-more-wrap {
    display: flex;
    justify-content: center;
    padding: 1rem 0 2rem;
  }
  .load-more-btn {
    background: oklch(0.72 0.28 310 / 0.14);
    border: 1px solid oklch(0.72 0.28 310 / 0.35);
    color: oklch(0.88 0.18 310);
    padding: 0.5rem 1.5rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.82rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .load-more-btn:hover:not(:disabled) {
    background: oklch(0.72 0.28 310 / 0.24);
    color: oklch(0.96 0.15 310);
  }
</style>
